import { useCallback, useEffect, useRef } from 'react';
import { containsExtent, isEmpty as isExtentEmpty } from 'ol/extent';
import { isKeyboardPanEvent } from '@utils/map/panDetection';

/**
 * Tracks whether the user has navigated the map themselves, and exposes
 * `fitToExtent` to auto-fit the view to a boundary layer's extent once per
 * `resetKey` (e.g. a project/model/collection load) — without fighting a
 * deliberate pan/zoom the user already made.
 *
 * @param {Object} mapObject - OpenLayers map instance
 * @param {string|null} resetKey - identifies the current "load"; auto-fit
 *   eligibility (userMoved tracking) resets whenever this changes
 */
export const useAutoFitBoundary = (mapObject, resetKey) => {
	// Tracks the load we've already auto-fitted for, so we fit once per load
	// and don't fight the user's zoom on filter changes or background refetches.
	const autoFitKeyRef = useRef(null);

	// Set once the user pans/zooms the map themselves. After that we never
	// auto-fit, so a late-resolving boundary can't override deliberate navigation.
	const userMovedRef = useRef(false);

	// Reset the flag when the load changes so the new context gets a fresh
	// chance to auto-fit before the user interacts with the map.
	useEffect(() => {
		userMovedRef.current = false;
	}, [resetKey]);

	// Flag genuine user gestures (drag-pan, wheel-zoom, double-click zoom,
	// keyboard pan) — not the programmatic view changes fitToExtent triggers,
	// which would false-positive if we listened to view change:center/
	// change:resolution instead.
	// Keyboard pan is routed to `document` (see keyboardEventTarget in
	// Map.jsx), not to the map's own target element, so that listener must be
	// attached to document rather than mapObject/viewport.
	useEffect(() => {
		if (!mapObject) return;

		const markMoved = () => {
			userMovedRef.current = true;
		};

		// Keyboard pan fires for arrow keys only, and only when focus isn't in
		// an editable field — matching KeyboardPan's own behavior so typing in
		// an unrelated form field (e.g. renaming a filter) doesn't get
		// mistaken for the user navigating the map.
		const markMovedOnPanKey = event => {
			if (isKeyboardPanEvent(event)) markMoved();
		};

		const viewport = mapObject.getViewport();
		mapObject.on('pointerdrag', markMoved);
		mapObject.on('dblclick', markMoved);
		viewport?.addEventListener('wheel', markMoved, { passive: true });
		document.addEventListener('keydown', markMovedOnPanKey);

		return () => {
			mapObject.un('pointerdrag', markMoved);
			mapObject.un('dblclick', markMoved);
			viewport?.removeEventListener('wheel', markMoved);
			document.removeEventListener('keydown', markMovedOnPanKey);
		};
	}, [mapObject]);

	/**
	 * Once per `resetKey`, if `layer`'s source extent extends beyond the
	 * current view, fits the map to it so the full boundary is visible
	 * without the user having to zoom out manually.
	 *
	 * @param {Object} layer - OL layer whose source extent to fit to; its
	 *   source must already reflect the current load (fit runs at most once
	 *   per distinct `resetKey`, and is skipped while the source is empty)
	 */
	// Memoized so it can be safely listed in a calling effect's dependency
	// array without that effect re-running on every render.
	const fitToExtent = useCallback(
		layer => {
			if (
				!layer ||
				!layer.getSource().getFeatures().length ||
				autoFitKeyRef.current === resetKey
			)
				return;

			const boundaryExtent = layer.getSource().getExtent();
			const size = mapObject?.getSize();
			const viewExtent = size ? mapObject.getView().calculateExtent(size) : null;

			// The map isn't sized yet — don't mark this resetKey as handled, so a
			// later call (once the map has a size) can still fit.
			if (!viewExtent) return;
			autoFitKeyRef.current = resetKey;

			const boundaryOutOfView =
				!userMovedRef.current &&
				boundaryExtent &&
				!isExtentEmpty(boundaryExtent) &&
				!containsExtent(viewExtent, boundaryExtent);

			if (boundaryOutOfView) {
				mapObject.getView().fit(boundaryExtent, {
					padding: [80, 80, 80, 80],
					duration: 600,
					maxZoom: 20,
				});
			}
		},
		[mapObject, resetKey]
	);

	return { fitToExtent };
};
